import { useCallback, useEffect, useMemo, forwardRef, Ref } from 'react';
import cn from 'classnames';
import ArrowTipUpIcon from '@stripcash/ui-kit/icons/arrowTipUp.svg';
import ArrowTipDownIcon from '@stripcash/ui-kit/icons/arrowTipDown.svg';
import { Link } from 'react-router-dom';

import { Category } from 'common/selectors/sidebar/utils';

import s from '../../styles.module.css';
import { SidebarViewProps } from '../..';

type ParentTabProps = {
  title: string;
  isParentGroup: boolean;
  isActiveGroup: boolean;
  hasNewSubgroups: boolean;
  expandedGroups: string[];
  href: string;
  Icon: any;
} & Pick<SidebarViewProps, 'collapsed' | 'activeGroup' | 'onExpand'> &
  Pick<Category, 'path'>;

const ParentTab = forwardRef<
  HTMLButtonElement | HTMLAnchorElement | undefined,
  ParentTabProps
>(
  (
    {
      expandedGroups,
      isParentGroup,
      isActiveGroup,
      activeGroup,
      path,
      title,
      collapsed,
      Icon,
      href,
      onExpand,
      hasNewSubgroups,
    },
    ref,
  ) => {
    const handleClick = useCallback(
      () => isParentGroup && onExpand(title),
      [isParentGroup, onExpand, title],
    );

    const baseProps = useMemo(
      () => ({
        className: cn(s.sidebarItem, s.groupItem, {
          [s.parentGroupItemActive]: isParentGroup && activeGroup === path,
          [s.groupItemActive]: isActiveGroup && !isParentGroup,
          [s.sidebarItemCollapsed]: collapsed,
        }),
        onClick: handleClick,
      }),
      [activeGroup, collapsed, handleClick, isActiveGroup, isParentGroup, path],
    );

    useEffect(() => {
      // if group without any subGroups - switch it off when leave
      if (
        !isParentGroup &&
        activeGroup !== path &&
        title &&
        expandedGroups.includes(title)
      ) {
        onExpand(title);
      }
    }, [activeGroup, expandedGroups, onExpand, path, isParentGroup, title]);

    const content = (
      <>
        {Icon && <Icon />}
        <span title={title}>{!collapsed && title}</span>
        {hasNewSubgroups && <div className={s.itemNewDot} />}
        {!collapsed && isParentGroup && (
          <div className={s.itemIcon}>
            {isActiveGroup ? <ArrowTipUpIcon /> : <ArrowTipDownIcon />}
          </div>
        )}
      </>
    );

    return isParentGroup ? (
      <button ref={ref as Ref<HTMLButtonElement>} {...baseProps} type="button">
        {content}
      </button>
    ) : (
      <Link ref={ref as never} {...baseProps} to={href}>
        {content}
      </Link>
    );
  },
);

export default ParentTab;
